import React from 'react';
import { CoverageProps, ProductItemOriginal, ProductItemProps } from '../../../../models/Product';
import { Page, useExecutiveCheckupRouter } from '../../router/context';
import { useScheduleContext } from '../../../../contexts/ScheduleContext';
import { ScheduleItemPropsBuilder, typeLocationSchedule } from '../../../../models/Schedule';
import { ProductSelectorTemplate } from '../../../../templates/ProductSelectorTemplate';
import { useServiceContext } from '../../../../contexts/ServiceContext';
import { PatientProps, usePatientContext } from '../../../../contexts/PatientContext';
import { fetchRuleValidation } from '../../../../modules/validateRules';
import { makePatientRuleByPatientProps } from '../../../../models/Patient';
import { LoadingOverlay } from '../../../../components';
import ModalNoEligibility from '../../../../components/ModalNoEligibility';
import { AxiosError } from 'axios';
import { isAxiosError } from '../../../../utils/errors';
import SystemUnavailable from '../../../../components/SystemUnavailable';
import { InternalStatusCode } from '../../../../utils/enums/validate-rules.enum';
import styled from 'styled-components';
import { ShowAlertByProduct } from './components/ShowAlertByProduct';
import { ConcurrentAppointmentsModal } from './components/ConcurrentAppointmentsModal';

const InfoWrapper = styled.div`
  margin-top: 24px;
`;

export function ProductList(state: any) {
  const { patientSelected } = usePatientContext();
  const [loading, setLoading] = React.useState(false);
  const [isModalNoEligibility, setIsModalNoEligibility] = React.useState(false);
  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);
  const [isModalConcurrentAppointmentsVisible, setIsModalConcurrentAppointmentsVisible] = React.useState(false);
  const [preventUser, setPreventUser] = React.useState(false);
  const [productName, setProductName] = React.useState('');
  const [telemedicineCoverage, setTelemedicineCoverage] = React.useState<CoverageProps | undefined>();

  const { goBack, navigate } = useExecutiveCheckupRouter();
  const { changeSchedule, selectedItem: schedule } = useScheduleContext();
  const { selectedService } = useServiceContext();

  function orderProductsByPriority(products: ProductItemProps[]) {
    return products.sort((product, product2) => {
      return Number(product.priority) - Number(product2.priority);
    });
  }

  const productList = React.useMemo(() => {
    return orderProductsByPriority(selectedService?.products);
  }, [selectedService?.products]);

  function handleError(message: string) {
    setPreventUser(true);

    switch (message) {
      case 'Concurrent appointments are already at the limit.':
        return setIsModalConcurrentAppointmentsVisible(true);

      case InternalStatusCode.MULTIPLE_APPOINTMENTS_EXCEPTION:
        return setIsModalConcurrentAppointmentsVisible(true);

      default:
        return setIsModalSystemUnavailableVisible(true);
    }
  }

  async function validateRules(props: {
    product: ProductItemProps;
    patient: PatientProps;
  }): Promise<{ product: ProductItemOriginal } | undefined> {
    setPreventUser(false);

    try {
      const response = await fetchRuleValidation({
        product: props.product,
        patient: makePatientRuleByPatientProps(props.patient),
        payment: { type: 'PARTICULAR' }
      });

      if (response.error && response.errorStatus) {
        handleError(response.errorStatus);
        return undefined;
      }

      const product = response.data?.data?.product;

      if (!product?.coverages?.length) {
        setIsModalNoEligibility(true);
        handleError('');
      } else return { product };
    } catch (e) {
      const error = e as AxiosError;

      if (isAxiosError(error)) {
        const message = error.response?.data.message;

        handleError(message);
      }
      console.error('Error trying validate service', error);
    }
  }

  function isProductOnlyTelemedicine(product: ProductItemProps | undefined) {
    if (product?.coverages?.length === 1) {
      const [coverage] = product.coverages;
      if (coverage.care_type === typeLocationSchedule.TELEMEDICINE) return coverage;
    }
  }

  async function createSchedule(product: ProductItemProps) {
    setLoading(true);
    setTelemedicineCoverage(undefined);
    setProductName(product.name);

    const scheduleFactory = ScheduleItemPropsBuilder();

    if (patientSelected) {
      const validated = await validateRules({ product, patient: patientSelected });

      const productValidated = validated?.product;

      if (productValidated) {
        scheduleFactory.setProduct({ ...productValidated, name: productValidated.name });
        scheduleFactory.setPayment({ type: 'PARTICULAR' });

        const telemedicineCoverage = isProductOnlyTelemedicine(productValidated);

        if (telemedicineCoverage?.care_type) {
          scheduleFactory.setChannel(telemedicineCoverage?.channel || '');
          scheduleFactory.setScheduleType({ careType: telemedicineCoverage.care_type });

          setTelemedicineCoverage(telemedicineCoverage);
        }
        changeSchedule({ ...schedule, ...scheduleFactory.build() });
      }
    }
    setLoading(false);
  }

  const handleSubmit = () => {
    if (telemedicineCoverage) navigate(Page.PRODUCT_SLOTS, { telemedicineCoverage });
    else navigate(Page.APPOINTMENT_TYPE, { ...state });
  };

  return (
    <>
      <ProductSelectorTemplate
        headTitle="Qual produto deseja agendar?"
        products={productList}
        handleProducts={_ => handleSubmit()}
        handleSelectProduct={product => createSchedule(product)}
        goBack={goBack}
        buttonDisabled={preventUser}
        listBottomComponent={
          <InfoWrapper>
            <ShowAlertByProduct productName={productName} />
          </InfoWrapper>
        }
      />
      <ModalNoEligibility visible={isModalNoEligibility} onClose={() => setIsModalNoEligibility(false)} />
      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
      />
      <ConcurrentAppointmentsModal
        visible={isModalConcurrentAppointmentsVisible}
        onClose={() => setIsModalConcurrentAppointmentsVisible(false)}
      />
      <LoadingOverlay loading={loading} />
    </>
  );
}
